import produce from 'immer';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {
  ProcedureType,
  ProcessClientOrigin,
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {makeNewEmptyMethodNode} from 'procedures/design/flowchartManagementUtils';
import {parseMethodNodeFromNetwork} from 'procedures/network/parseProcedureFromNetwork';
import {MethodNodeREST} from 'procedures/network/ProcedureDAO';
import {serializeMethodNodeForNetwork} from 'procedures/network/serializeProcedureFlowchartForNetwork';
import {MethodNode} from 'procedures/ProcedureDomainTypes';
import {getFieldClientId, ProcedureFieldGroupKey} from 'procedures/processes/DataFlowEdgeUtils';
import MethodSidepanelEditor from 'procedures/processes/MethodSidepanelEditor';
import {
  createFieldDefinitionsFromMethodNode,
  getFieldsForGroup,
  ProcedureFieldInfo,
} from 'procedures/processes/ProcedureFieldDefinitionUtils';
import {getIncrementedNodeName} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {
  FlowchartNodeEditMode,
  NodeCreationContext,
  ProcessFlowchartTaskSchemaNodeAdapter,
  TaskSchemaDisplayOptions,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {
  NodeSettingsTabKeys,
  ProcessTaskDefinitionVersionGraphQL,
  SidepanelTab,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeRowData,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {SidepanelSize} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {formatSetpointDisplayForNode} from './MethodNodeUtils';

type ProcedureSectionConfig = Pick<TaskSchemaNodeSectionData, 'sectionName' | 'sectionType'>;

/**
 * Configuration for procedure node sections, defining how each field group
 * should be displayed and categorized in the flowchart node.
 */
const PROCEDURE_SECTION_CONFIGURATION_BY_FIELD_GROUP_KEY: Record<
  ProcedureFieldGroupKey,
  ProcedureSectionConfig
> = {
  [ProcedureFieldGroupKey.MATERIAL_INPUTS]: {
    sectionName: 'Material Inputs',
    sectionType: TaskSchemaNodeSectionType.INPUTS,
  },
  [ProcedureFieldGroupKey.MATERIAL_OUTPUTS]: {
    sectionName: 'Material Outputs',
    sectionType: TaskSchemaNodeSectionType.OUTPUTS,
  },
  [ProcedureFieldGroupKey.DATA_PARAMETERS]: {
    sectionName: 'Parameters',
    sectionType: TaskSchemaNodeSectionType.NOT_MAPPABLE,
  },
  [ProcedureFieldGroupKey.EQUIPMENT]: {
    sectionName: 'Equipment',
    sectionType: TaskSchemaNodeSectionType.NOT_MAPPABLE,
  },
};

/**
 * Creates a row data object for a field definition.
 *
 * @param field - The field definition from GraphQL
 * @param sectionName - Display name for the section containing this field
 * @param sectionType - Type of section (inputs, outputs, or not mappable)
 * @returns Formatted row data for the task schema node renderer
 */
function createSectionRow(
  field: ProcedureFieldInfo,
  fieldGroupKey: ProcedureFieldGroupKey
): TaskSchemaNodeRowData {
  const {icon, label} = getIconAndLabel({
    field: {
      fieldType: field.dataType.type,
      requiredLink: field.dataType.requiredLink,
      unitSymbol: field.dataType.unit?.symbol ?? null,
      unitApiIdentifier: field.dataType.unit?.id ?? null,
    },
    schemaFieldSelectors: [],
  });
  const fieldClientId = getFieldClientId(fieldGroupKey, field.name);
  const {sectionName, sectionType} = PROCEDURE_SECTION_CONFIGURATION_BY_FIELD_GROUP_KEY[fieldGroupKey];
  return {
    id: uuidv4(),
    clientId: fieldClientId,
    fieldName: field.name,
    fieldIcon: icon,
    fieldTypeLabel: label,
    sectionName,
    sectionType,
    isFlowchartTask: false,
    secondaryText:
      formatSetpointDisplayForNode(field.values, field.dataType.unit?.symbol, field.dataType.type) ??
      undefined,
  };
}

/**
 * Adapter for rendering procedure method nodes in the Process Flowchart Editor.
 *
 * Converts schema fields to field definitions since PTDVs use field definitions but procedure editors use schema fields.
 * Ideally, we'd use field definitions everywhere, but there isn't full support for them as of yet.
 */
export default class ProcedureMethodNodeAdapter extends ProcessFlowchartTaskSchemaNodeAdapter<
  MethodNode,
  MethodNodeREST
> {
  readonly executionType: ProcessExecutionType = ProcessExecutionType.PROCEDURE_METHOD;
  readonly typeDisplayName: string = 'unit operation';
  readonly editMode = FlowchartNodeEditMode.DOUBLE_CLICK;
  override readonly isNodeNameEditableInline = true;

  nodeDetailsSidepanelContent: Array<SidepanelTab> = [
    {
      key: NodeSettingsTabKeys.TASK_SCHEMA_DETAILS,
      label: 'Method',
      content: MethodSidepanelEditor,
    },
  ];

  convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): MethodNode {
    const applicationData = taskDefinitionVersionGQL.applicationData;
    assert(applicationData != null, 'Application data is required');
    assert(
      applicationData.__typename === 'ProcedureMethodDefinitionVersionNode',
      'Application data is not a method node'
    );
    return parseMethodNodeFromNetwork(applicationData, (id, errorContext) => id);
  }

  convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    flowchartConfigId: string
  ): MethodNodeREST {
    assert(
      taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData != null,
      'Application data is required'
    );
    return serializeMethodNodeForNetwork(
      taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData,
      undefined,
      flowchartConfigId
    );
  }

  /**
   * Extracts the display name for a procedure node from its task definition version.
   * Expects the application data to be a Procedure method definition version.
   *
   * @param taskDefinitionVersion - The task definition version to extract name from
   * @returns The display name for the procedure node
   * @throws Error if applicationData is null or not a ProcedureMethodDefinitionVersionNode
   */
  getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    isFlowchartOutput?: boolean,
    existingNodeNames?: Array<string>
  ): string {
    const applicationData = taskDefinitionVersion.newProcessTaskDefinitionVersion.applicationData;

    assert(
      applicationData != null,
      `applicationData is required for task definition version ${taskDefinitionVersion.clientId}`
    );
    return applicationData.name;
  }

  getNodeSubtitle(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    isFlowchartOutput?: boolean
  ): string | null {
    return null;
  }

  getNodeIcon(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    isFlowchartOutput?: boolean
  ): BenchlingIconName | null {
    return null;
  }

  /**
   * Creates section data for rendering the procedure node's field groups.
   * Organizes input fields and output fields into logical sections (materials, parameters, equipment)
   * based on their field group keys.
   *
   * @param taskDefinitionVersion - The task definition version containing field definitions
   * @param nodeDetailsType - The type of node details (unused in this implementation)
   * @returns Array of section data for the task schema node renderer
   */
  createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData> {
    const applicationData = taskDefinitionVersion.newProcessTaskDefinitionVersion.applicationData;
    if (applicationData == null) {
      return [];
    }

    return Object.values(ProcedureFieldGroupKey).map((fieldGroupKey) => {
      const {sectionName, sectionType} = PROCEDURE_SECTION_CONFIGURATION_BY_FIELD_GROUP_KEY[fieldGroupKey];
      const methodFields = getFieldsForGroup(applicationData, fieldGroupKey);
      return {
        sectionName,
        sectionType,
        sectionRows: methodFields.map((field) => createSectionRow(field, fieldGroupKey)),
        hideSection: false,
      };
    });
  }

  override updateTaskDefinitionVersionWithApplicationData(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>
  ): TaskDefinitionVersionSpecWithTaskSchema<MethodNode> {
    const applicationData = taskDefinitionVersion.newProcessTaskDefinitionVersion.applicationData;
    if (applicationData == null) {
      return taskDefinitionVersion;
    }

    const {inputFieldDefinitions, outputFieldDefinitions} =
      createFieldDefinitionsFromMethodNode(applicationData);

    return {
      ...taskDefinitionVersion,
      inputFieldDefinitions,
      outputFieldDefinitions,
    };
  }

  onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<MethodNode> | null> {
    assert(context.singletonTaskSchemaForExecutionType != null, 'Singleton task schema is required');

    const newMethodCid = uuidv4();
    const newMethodNode: MethodNode = makeNewEmptyMethodNode(
      context.rootTaskSchema.clientOrigin === ProcessClientOrigin.RECIPES
        ? ProcedureType.RECIPE
        : ProcedureType.ASSAY,
      0,
      0,
      newMethodCid
    );
    newMethodNode.name = getIncrementedNodeName('New unit operation', context.existingNodeNames);
    const newTaskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<MethodNode> = {
      clientId: uuidv4(),
      inputFieldDefinitions: [],
      outputFieldDefinitions: null,
      newProcessTaskDefinitionVersion: {
        applicationData: newMethodNode,
        taskSchemaId: context.singletonTaskSchemaForExecutionType.id,
        taskSchema: context.singletonTaskSchemaForExecutionType,
      },
      currentVersionId: null,
    };
    return Promise.resolve(newTaskDefinitionVersionSpec);
  }

  getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  } {
    return {
      nodeName: 'New unit operation',
      nodeSubtitle: null,
      nodeIcon: null,
    };
  }

  getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  ): ProcessSingletonTaskSchemaName | null {
    if (rootTaskSchema.clientOrigin === ProcessClientOrigin.RECIPES) {
      return ProcessSingletonTaskSchemaName.RECIPE_UNIT_OPERATION_TASK_SCHEMA_NAME;
    } else {
      return ProcessSingletonTaskSchemaName.ASSAY_UNIT_OPERATION_TASK_SCHEMA_NAME;
    }
  }

  override getDisplayOptions(): TaskSchemaDisplayOptions {
    return {
      rightSidepanelSize: SidepanelSize.FULL,
      rightSidepanelNameOverride: (nodeName: string) => nodeName,
    };
  }

  /**
   * Updates the task definition version spec with a new node name.
   * Called when the user edits the node name via double-click on the canvas
   * (when `isNodeNameEditableInline` is true).
   */
  override onUpdateNodeName(
    currentSpec: TaskDefinitionVersionSpecWithTaskSchema<MethodNode>,
    newName: string
  ): TaskDefinitionVersionSpecWithTaskSchema<MethodNode> {
    assert(
      this.isNodeNameEditableInline,
      'Node name double click editing is not enabled for procedure method nodes'
    );
    const currentApplicationData = currentSpec.newProcessTaskDefinitionVersion.applicationData;
    assert(currentApplicationData != null, 'Application data is required');

    return produce(currentSpec, (draftSpec) => {
      draftSpec.newProcessTaskDefinitionVersion.applicationData!.name = newName;
    });
  }
}
